import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Download, Loader2, LogOut, Mail, Paperclip, Phone, Send } from "lucide-react";
import { CmykBar, Crosshair, RulerStrip } from "../components/Marks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ngs_admin_token";

const formatDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
};

const LoginView = ({ onToken }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onToken(data.access_token);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-5">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="relative w-full max-w-md border border-[#2a2a2a] bg-[#111] p-8 sm:p-10"
      >
        <CmykBar className="absolute inset-x-0 top-0" />
        <Crosshair size={22} className="text-[#e62b2b]" />
        <h1 className="display-xl mt-6 text-4xl">STUDIO INBOX</h1>
        <p className="mt-3 font-mono-tech text-[0.65rem] uppercase tracking-[0.24em] text-neutral-500">
          PRIVATE — NO GLYPH STUDIOS
        </p>

        <label className="mt-9 block">
          <span className="label-mono block text-neutral-500">EMAIL</span>
          <input
            data-testid="admin-email"
            className="ng-field"
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="mt-6 block">
          <span className="label-mono block text-neutral-500">PASSWORD</span>
          <input
            data-testid="admin-password"
            className="ng-field"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>

        {error && (
          <p
            data-testid="admin-login-error"
            role="alert"
            className="mt-6 font-mono-tech text-xs text-[#e62b2b]"
          >
            {error}
          </p>
        )}

        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={busy}
          className="mt-9 flex w-full items-center justify-center gap-3 py-4 font-display text-2xl font-extrabold uppercase tracking-tight text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
          style={{ background: "var(--ng-red)" }}
        >
          {busy && <Loader2 size={20} className="animate-spin" />}
          {busy ? "SIGNING IN" : "SIGN IN"}
        </button>

        <Link
          data-testid="admin-back-to-site"
          to="/"
          className="label-mono mt-8 block text-neutral-500 transition-colors hover:text-white"
        >
          ← BACK TO SITE
        </Link>
      </form>
    </div>
  );
};

const STATUSES = ["New", "Quoted", "Done"];
const STATUS_COLOR = {
  New: "var(--ng-red)",
  Quoted: "var(--ng-blue-bright)",
  Done: "#3f9c58",
};

const Row = ({ inquiry, token, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [dlError, setDlError] = useState("");
  const [dlBusy, setDlBusy] = useState("");
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState("");
  const [replySent, setReplySent] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const setStatus = async (status) => {
    setStatusBusy(true);
    try {
      const { data } = await axios.patch(
        `${API}/inquiries/${inquiry.inquiry_id}/status`,
        { status },
        auth
      );
      onUpdate(data);
    } catch (err) {
      setReplyError(formatDetail(err.response?.data?.detail));
    } finally {
      setStatusBusy(false);
    }
  };

  const sendReply = async () => {
    setSending(true);
    setReplyError("");
    try {
      const { data } = await axios.post(
        `${API}/inquiries/${inquiry.inquiry_id}/reply`,
        { message: reply },
        auth
      );
      onUpdate(data);
      setReply("");
      setReplySent(true);
    } catch (err) {
      setReplyError(formatDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const download = async (file) => {
    setDlError("");
    setDlBusy(file.file_id);
    try {
      const res = await axios.get(`${API}/files/${file.file_id}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDlError(`Could not download ${file.original_filename}. Please try again.`);
    } finally {
      setDlBusy("");
    }
  };

  return (
    <li
      data-testid={`inquiry-row-${inquiry.inquiry_id}`}
      className="border-b border-[#222]"
    >
      <button
        data-testid={`inquiry-toggle-${inquiry.inquiry_id}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="grid w-full grid-cols-1 gap-2 py-5 text-left transition-colors hover:bg-[#141414] sm:grid-cols-12 sm:items-baseline sm:gap-4 sm:px-4"
      >
        <span className="font-mono-tech text-[0.65rem] uppercase tracking-[0.18em] text-neutral-400 sm:col-span-2">
          {new Date(inquiry.created_at).toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
        <span className="font-display text-xl font-extrabold uppercase tracking-tight text-white sm:col-span-3">
          {inquiry.name}
        </span>
        <span className="truncate text-sm text-neutral-400 sm:col-span-2">
          {inquiry.email}
        </span>
        <span className="font-mono-tech text-[0.65rem] uppercase tracking-[0.14em] text-neutral-500 sm:col-span-2">
          {inquiry.need_help_with || "—"}
        </span>
        <span
          data-testid={`status-pill-${inquiry.inquiry_id}`}
          className="label-mono w-fit px-2.5 py-1 font-bold text-white sm:col-span-2"
          style={{ background: STATUS_COLOR[inquiry.status] || "var(--ng-red)" }}
        >
          {inquiry.status || "New"}
        </span>
        <span className="flex items-center gap-1 font-mono-tech text-[0.65rem] text-neutral-400 sm:col-span-1 sm:justify-end">
          {inquiry.files?.length > 0 && (
            <>
              <Paperclip size={12} />
              {inquiry.files.length}
            </>
          )}
        </span>
      </button>

      {open && (
        <div
          data-testid={`inquiry-detail-${inquiry.inquiry_id}`}
          className="grid gap-8 border-t border-[#1c1c1c] bg-[#0e0e0e] px-4 py-8 sm:grid-cols-12"
        >
          <div className="space-y-3 sm:col-span-4">
            <a
              href={`mailto:${inquiry.email}`}
              className="flex items-center gap-2 text-sm text-neutral-300 transition-colors hover:text-[#0055ff]"
            >
              <Mail size={14} /> {inquiry.email}
            </a>
            {inquiry.phone && (
              <p className="flex items-center gap-2 text-sm text-neutral-300">
                <Phone size={14} /> {inquiry.phone}
              </p>
            )}
            <p className="label-mono text-neutral-500">
              {inquiry.brand_name || "NO BRAND GIVEN"}
            </p>
            <p className="label-mono text-neutral-500">
              {inquiry.timeline_flexible
                ? "TIMELINE FLEXIBLE"
                : inquiry.timeline
                  ? `NEEDED BY ${inquiry.timeline}`
                  : "NO TIMELINE GIVEN"}
            </p>
            <p className="label-mono text-neutral-300">
              EMAIL NOTIFICATION: {inquiry.email_status} · AUTO-REPLY:{" "}
              {inquiry.autoreply_status}
            </p>

            <div className="pt-4">
              <span className="label-mono block text-neutral-500">STATUS</span>
              <div className="mt-3 flex gap-2">
                {STATUSES.map((s) => {
                  const active = (inquiry.status || "New") === s;
                  return (
                    <button
                      key={s}
                      data-testid={`set-status-${s.toLowerCase()}-${inquiry.inquiry_id}`}
                      onClick={() => setStatus(s)}
                      disabled={statusBusy || active}
                      aria-pressed={active}
                      className="label-mono border px-3 py-2 font-bold transition-colors disabled:cursor-default"
                      style={{
                        borderColor: active ? STATUS_COLOR[s] : "#3a3a3a",
                        background: active ? STATUS_COLOR[s] : "transparent",
                        color: active ? "#fff" : "#a3a3a3",
                      }}
                    >
                      {s.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="sm:col-span-8">
            <p className="whitespace-pre-line text-base leading-relaxed text-neutral-200">
              {inquiry.message}
            </p>
            {inquiry.files?.length > 0 && (
              <ul className="mt-6 space-y-2">
                {inquiry.files.map((f) => (
                  <li key={f.file_id}>
                    <button
                      data-testid={`download-${f.file_id}`}
                      onClick={() => download(f)}
                      disabled={dlBusy === f.file_id}
                      className="flex w-full items-center justify-between border border-[#2f2f2f] px-4 py-3 font-mono-tech text-xs text-neutral-300 transition-colors hover:border-[#0055ff] hover:text-white disabled:opacity-60"
                    >
                      {f.original_filename}
                      {dlBusy === f.file_id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Download size={14} />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {dlError && (
              <p
                data-testid="download-error"
                role="alert"
                className="mt-4 font-mono-tech text-xs text-[#e62b2b]"
              >
                {dlError}
              </p>
            )}

            {inquiry.replies?.length > 0 && (
              <ul
                data-testid={`reply-history-${inquiry.inquiry_id}`}
                className="mt-8 space-y-3"
              >
                {inquiry.replies.map((r) => (
                  <li
                    key={r.sent_at}
                    className="border-l-2 pl-4"
                    style={{ borderColor: "var(--ng-blue-bright)" }}
                  >
                    <span className="label-mono text-neutral-500">
                      SENT {new Date(r.sent_at).toLocaleString()}
                    </span>
                    <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-neutral-300">
                      {r.message}
                    </p>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-8 border-t border-[#1f1f1f] pt-6">
              <label className="label-mono block text-neutral-500">
                REPLY TO {inquiry.name.toUpperCase()}
                <textarea
                  data-testid={`reply-input-${inquiry.inquiry_id}`}
                  className="ng-field mt-2 resize-y"
                  rows={4}
                  placeholder="Type your reply — it emails them from No Glyph Studios."
                  value={reply}
                  onChange={(e) => {
                    setReply(e.target.value);
                    setReplySent(false);
                  }}
                />
              </label>
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <button
                  data-testid={`reply-send-${inquiry.inquiry_id}`}
                  onClick={sendReply}
                  disabled={sending || !reply.trim()}
                  className="flex items-center gap-3 px-6 py-3 font-display text-lg font-extrabold uppercase tracking-tight text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:translate-y-0 disabled:opacity-50"
                  style={{ background: "var(--ng-red)" }}
                >
                  {sending ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Send size={16} />
                  )}
                  {sending ? "SENDING" : "SEND REPLY"}
                </button>
                {replySent && (
                  <span
                    data-testid={`reply-sent-${inquiry.inquiry_id}`}
                    className="label-mono text-[#0055ff]"
                  >
                    REPLY SENT
                  </span>
                )}
              </div>
              {replyError && (
                <p
                  data-testid={`reply-error-${inquiry.inquiry_id}`}
                  role="alert"
                  className="mt-4 font-mono-tech text-xs text-[#e62b2b]"
                >
                  {replyError}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </li>
  );
};

export default function Inbox() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [inquiries, setInquiries] = useState(null);
  const [error, setError] = useState("");

  const signOut = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setInquiries(null);
  }, []);

  useEffect(() => {
    if (!token) return;
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/inquiries`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (active) setInquiries(data);
      } catch (err) {
        if (!active) return;
        if (err.response?.status === 401) signOut();
        else setError(formatDetail(err.response?.data?.detail));
      }
    })();
    return () => {
      active = false;
    };
  }, [token, signOut]);

  if (!token) return <LoginView onToken={setToken} />;

  return (
    <div data-testid="admin-inbox" className="min-h-screen bg-[#0a0a0a] pb-24">
      <header className="border-b border-[#2a2a2a] px-5 py-6 sm:px-8">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="display-xl text-3xl sm:text-4xl">STUDIO INBOX</h1>
            <p className="mt-2 font-mono-tech text-[0.65rem] uppercase tracking-[0.24em] text-neutral-500">
              PROJECT INQUIRIES —{" "}
              {inquiries ? `${inquiries.length} TOTAL` : "LOADING"}
            </p>
          </div>
          <div className="flex items-center gap-5">
            <Link
              data-testid="admin-view-site"
              to="/"
              className="label-mono text-neutral-400 transition-colors hover:text-white"
            >
              VIEW SITE
            </Link>
            <button
              data-testid="admin-logout"
              onClick={signOut}
              className="flex items-center gap-2 border border-[#3a3a3a] px-4 py-2.5 font-mono-tech text-[0.65rem] uppercase tracking-[0.18em] text-neutral-300 transition-colors hover:border-[#e62b2b] hover:text-white"
            >
              <LogOut size={13} /> SIGN OUT
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <RulerStrip className="mt-10" color="#1f1f1f" />

        {error && (
          <p data-testid="admin-error" role="alert" className="mt-8 font-mono-tech text-xs text-[#e62b2b]">
            {error}
          </p>
        )}

        {!inquiries && !error && (
          <div className="flex items-center gap-3 py-16 font-mono-tech text-xs text-neutral-500">
            <Loader2 size={16} className="animate-spin" /> LOADING INQUIRIES
          </div>
        )}

        {inquiries?.length === 0 && (
          <div
            data-testid="admin-empty-state"
            className="flex flex-col items-center gap-5 border border-[#222] py-24"
          >
            <Crosshair size={28} className="text-neutral-700" />
            <p className="font-display text-2xl font-extrabold uppercase tracking-tight text-neutral-500">
              No inquiries yet
            </p>
          </div>
        )}

        {inquiries?.length > 0 && (
          <>
            <div className="hidden grid-cols-12 gap-4 border-b border-[#2a2a2a] px-4 pb-3 pt-6 sm:grid">
              {[
                ["RECEIVED", "col-span-2"],
                ["NAME", "col-span-3"],
                ["EMAIL", "col-span-2"],
                ["NEEDS HELP WITH", "col-span-2"],
                ["STATUS", "col-span-2"],
                ["FILES", "col-span-1 text-right"],
              ].map(([label, span]) => (
                <span
                  key={label}
                  className={`label-mono text-neutral-500 ${span}`}
                >
                  {label}
                </span>
              ))}
            </div>
            <ul data-testid="admin-inquiry-list">
              {inquiries.map((i) => (
                <Row
                key={i.inquiry_id}
                inquiry={i}
                token={token}
                onUpdate={(updated) =>
                  setInquiries((prev) =>
                    prev.map((x) =>
                      x.inquiry_id === updated.inquiry_id ? updated : x
                    )
                  )
                }
              />
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
